import { ClientToServerEvents, ServerToClientEvents } from '@shared/types';
import { call, put, select, take, takeEvery, takeLatest } from 'modules/typed-saga';
import { Action } from 'redux';
import { eventChannel } from 'redux-saga';
import io, { Socket } from 'socket.io-client';
import { parseErrorData } from 'utils';
import { workerErrorNotifySaga, workerErrorNotifyThunk } from 'utils/sagas';
import { actionAccountLogout } from '../../../store/accounts';
import { Conversation, ServiceChat } from '../service';
import {
  actionChatConversationDelete,
  actionChatConversationGet,
  actionChatMessageGet,
  actionChatSendMessage,
  actionChatStartConversation,
} from './actions';
import { selectChatConversationsList, selectChatStatuses } from './selectors';
import {
  actionChatCameMessage,
  actionChatConversationOpen,
  actionChatConversationWasDeleted,
  actionChatGetConversations,
  actionChatStartChat,
  actionChatStartChatNew,
  actionChatTyping,
  actionChatTypingCame,
  actionChatUserConnected,
  actionChatUserDisconnected,
  actionCustomChatUsersListSet,
} from './slice';

export const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(
  process.env.REACT_APP_CHAT_SERVER as string,
  {
    transports: ['websocket'],
    autoConnect: false,
  },
);

function getSocketChannel() {
  return eventChannel((emitter) => {
    socket.on('connect', () => {
      emitter(actionChatGetConversations.request());
    });
    socket.on('users', (userIDs) => {
      emitter(actionCustomChatUsersListSet(userIDs));
    });
    socket.on('user_connected', (userID) => {
      emitter(actionChatUserConnected(userID));
    });
    socket.on('user_disconnected', (userID) => {
      emitter(actionChatUserDisconnected(userID));
    });

    socket.on('delete', (payload: { conversationID: string }) => {
      emitter(actionChatConversationWasDeleted(payload));
    });
    socket.on('message', (payload: { conversationID: string; messageID: string }) => {
      emitter(actionChatCameMessage(payload));
    });

    socket.on('typing', (payload) => {
      emitter(actionChatTypingCame(payload));
    });

    socket.on('connect_error', (error) => {
      console.error(error);
    });
    socket.onAny((event, ...args) => {
      process.env.NODE_ENV === 'development' && console.log(event, args);
    });

    return () => {
      socket.removeListener('connect');
      socket.removeListener('disconnect');
      socket.removeListener('users');
      socket.removeListener('user_connected');
      socket.removeListener('user_disconnected');
      socket.removeListener('delete');
      socket.removeListener('message');
      socket.removeListener('typing');
    };
  });
}

function* chatChannelSagas() {
  const channel = yield* call(getSocketChannel);

  while (true) {
    const action = yield* take(channel);
    yield* put(action as Action);
  }
}

function* fetchConversations() {
  try {
    const {
      data: { value },
    } = yield* call(ServiceChat.getConversations);
    yield* put(actionChatGetConversations.success({ data: value }));
  } catch (e: any) {
    yield* put(actionChatGetConversations.fail({ error: parseErrorData(e) }));
  }
}

function watchSuccessDeleteConversation(
  action: ReturnType<typeof actionChatConversationDelete.fulfilled>,
) {
  const { conversationID, conversation } = action.payload;

  if (conversation) {
    socket.emit('delete', { toAppUserID: conversation.appUserID, conversationID });
  }
}

function watchSuccessSendMessage(
  action: ReturnType<
    typeof actionChatStartConversation.fulfilled | typeof actionChatSendMessage.fulfilled
  >,
) {
  const { appUserID, message, conversationID } = action.payload;

  socket.emit('message', {
    toAppUserID: appUserID,
    conversationID,
    messageID: message.conversationMessageID,
  });
}

function watchType(action: ReturnType<typeof actionChatTyping>) {
  const { appUserID, conversationID, value } = action.payload;
  socket.emit('typing', { toAppUserID: appUserID, conversationID, typing: value });
}

function* watchCameMessage(action: ReturnType<typeof actionChatCameMessage>) {
  const { conversationID, messageID } = action.payload;

  const stateItemList = yield* select(selectChatConversationsList);
  const stateItem = stateItemList.find(
    ({ conversation }) => conversation.conversationID === conversationID,
  );

  if (!stateItem) {
    yield* put(actionChatConversationGet({ conversationID }));
  } else if (stateItem.isInit) {
    yield* put(actionChatMessageGet({ conversationID, messageID }));
  }
}

function* watchStartChat(action: ReturnType<typeof actionChatStartChat>) {
  const { appUserID } = action.payload;

  let conversation: Conversation | null = null;

  const { isInit } = yield* select(selectChatStatuses);

  // wait for conversations
  if (!isInit) {
    yield* take(actionChatGetConversations.success);
  }

  const listStateItems = yield* select(selectChatConversationsList);

  const stateItem = listStateItems.find(({ conversation }) => conversation.appUserID === appUserID);

  conversation = stateItem ? stateItem.conversation : conversation;

  if (conversation) {
    yield* put(actionChatConversationOpen({ conversationID: conversation.conversationID }));
  } else {
    yield* put(actionChatStartChatNew(action.payload));
  }
}

function watchLogout() {
  socket.disconnect();
}

export const chatSagas = [
  chatChannelSagas(),
  takeLatest(actionAccountLogout.fulfilled, watchLogout),
  takeLatest(actionChatGetConversations.request, fetchConversations),

  takeLatest(actionChatConversationDelete.fulfilled, watchSuccessDeleteConversation),

  takeLatest(
    [actionChatSendMessage.fulfilled, actionChatStartConversation.fulfilled],
    watchSuccessSendMessage,
  ),

  takeLatest(actionChatCameMessage, watchCameMessage),
  takeLatest(actionChatTyping, watchType),
  takeLatest(actionChatStartChat, watchStartChat),

  takeEvery([actionChatGetConversations.fail], workerErrorNotifySaga),

  takeEvery(
    [
      actionChatConversationGet.rejected,
      actionChatMessageGet.rejected,
      actionChatConversationDelete.rejected,
      actionChatSendMessage.rejected,
      actionChatStartConversation.rejected,
    ],
    workerErrorNotifyThunk,
  ),
];
